import { Currency, isCurrency } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { FxContext, useFxContextValue } from "@redotech/redo-web/fx/fx-context";
import { FxWatchRates } from "@redotech/redo-web/fx/fx-subscription-store";
import { memo, ReactNode, useContext, useMemo } from "react";

/** No live rates until the RPC client is ready (e.g. before auth). */
const noRates: FxWatchRates = async function* () {};

/**
 * The merchant app's FX provider: the single root-mounted instance driving every
 * {@link MoneyText}. Derives store currency from the team and builds the live-rate
 * transport from the merchant RPC client's `watchFxRates` stream. Mount once near
 * the root, below the team + rpc-client contexts.
 */
export const FxProvider = memo(function FxProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useContext(RedoMerchantRpcClientContext);
  // Remount once the client is available so subscriptions opened before auth
  // rebuild against the real transport.
  return (
    <FxProviderInner key={rpcClient ? "ready" : "pending"}>
      {children}
    </FxProviderInner>
  );
});

const FxProviderInner = memo(function FxProviderInner({
  children,
}: {
  children: ReactNode;
}) {
  const team = useContext(TeamContext);
  const rpcClient = useContext(RedoMerchantRpcClientContext);

  const storeCurrency = useMemo<Currency>(() => {
    const code = team?._shopify?.currency;
    return code && isCurrency(code) ? code : Currency.USD;
  }, [team]);

  // Stable identity (always calls the latest rpcClient) so the subscription
  // store is built once per mount.
  const watchRates = useHandler<FxWatchRates>((refs, signal) =>
    rpcClient
      ? rpcClient.watchFxRates({ refs }, { signal })
      : noRates(refs, signal),
  );

  const value = useFxContextValue(watchRates, storeCurrency);
  return <FxContext.Provider value={value}>{children}</FxContext.Provider>;
});
